import type { Media } from '../api/types'

/**
 * Ein Foto aus der Anwendung heraus sichern.
 *
 * **Warum das nicht der Browser erledigt.** Auf iOS legt ein langes Drücken
 * das Bild in „Fotos"; auf Android passiert dabei nichts (#58). Und wo Chrome
 * dort doch ein Menü zeigt, landet die Datei im Ordner *Downloads* und nicht
 * in der Galerie — das ist nicht dasselbe. Eine Geste, die auf dem einen Gerät
 * etwas anderes tut als auf dem anderen und auf dem dritten gar nichts, ist
 * keine Bedienung, auf die sich eine Anwendung stützen kann.
 *
 * **Zwei Wege, in dieser Reihenfolge:**
 *
 *  1. **Das Gerät fragen** (`navigator.share` mit Datei). Das ist auf beiden
 *     Telefonen der richtige Weg: Android öffnet sein Teilen-Blatt samt
 *     „Fotos", iOS seines samt „Bild sichern". Ein Weg, zwei Systeme, und in
 *     beiden das, was die Leute von dort kennen.
 *  2. **Herunterladen**, wo es das nicht gibt — an jedem Rechner. Dort ist
 *     eine Datei im Download-Ordner genau das Erwartete.
 *
 * **Geholt wird über dieselbe Adresse wie das angezeigte Bild**, also durch
 * `Visibility` hindurch. Das ist kein Umweg, sondern der Punkt: es gibt keinen
 * zweiten Weg zu einer Datei, und dieser hier bekommt keinen.
 */
export type Weg = 'geteilt' | 'geladen' | 'abgebrochen'

export async function sichereFoto(media: Media): Promise<Weg> {
  // Same-Origin: das Session-Cookie geht von selbst mit. Und das Bild steht
  // schon im Zwischenspeicher des Browsers -- es wird gerade angezeigt --,
  // deshalb ist das im Regelfall keine zweite Übertragung.
  const antwort = await fetch(media.url)
  if (!antwort.ok) throw new Error(`Der Server antwortete mit ${antwort.status}.`)
  const inhalt = await antwort.blob()
  const datei = new File([inhalt], dateiname(media), { type: inhalt.type || media.mime })

  /*
   * **`canShare` mit der Datei fragen, nicht nur nach `share`.** Ein Browser
   * kann Text teilen und Dateien nicht; wer nur prüft, ob es `navigator.share`
   * gibt, bekommt am Rechner eine Ausnahme statt eines Downloads.
   */
  if (navigator.canShare?.({ files: [datei] }) === true) {
    try {
      await navigator.share({ files: [datei] })
      return 'geteilt'
    } catch (fehler) {
      // Wer das Blatt zumacht, hat nicht gescheitert -- er wollte nicht.
      if (abgebrochen(fehler)) return 'abgebrochen'
      // Alles andere ist ein echter Fehlschlag des einen Weges; dann bleibt
      // der andere. Eine Fehlermeldung, wo ein Download getan hätte, wäre
      // eine stille Absage mit Text.
    }
  }

  const adresse = URL.createObjectURL(inhalt)
  const verweis = document.createElement('a')
  verweis.href = adresse
  verweis.download = datei.name
  document.body.append(verweis)
  verweis.click()
  verweis.remove()
  // Erst freigeben, wenn der Browser den Download angefasst hat. Sofort
  // widerrufen heisst auf manchen Geräten: leere Datei.
  setTimeout(() => URL.revokeObjectURL(adresse), 60_000)
  return 'geladen'
}

/**
 * „reisefoto-42.jpg" — ein Name, den man wiederfindet.
 *
 * Der ursprüngliche Dateiname liegt nirgends: hochgeladen wird ein Bild, das
 * der Browser vorher verkleinert hat, und `IMG_4711.HEIC` beschreibt danach
 * ohnehin etwas anderes. Die Nummer ist dafür eindeutig, und die Endung kommt
 * aus dem Typ, den der Server nennt.
 */
export function dateiname(media: Media): string {
  return `reisefoto-${media.id}.${endung(media.mime)}`
}

const ENDUNGEN: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'image/heif': 'heif',
}

function endung(mime: string): string {
  const bekannt = ENDUNGEN[mime.toLowerCase()]
  if (bekannt !== undefined) return bekannt
  // `image/etwas-neues` -- der hintere Teil ist als Endung besser als „bin".
  const hinten = mime.split('/')[1]?.replace(/[^a-z0-9]/gi, '') ?? ''
  return hinten === '' ? 'jpg' : hinten.toLowerCase()
}

/** Das Teilen-Blatt zugemacht — kein Fehler, sondern eine Entscheidung. */
function abgebrochen(fehler: unknown): boolean {
  return fehler instanceof DOMException && fehler.name === 'AbortError'
}
